import React, { useCallback, useMemo, useState } from 'react';
import { Stack, Typography } from '@mui/material';
import { AddCircleRounded, Delete } from '@mui/icons-material';
import { TeamUsers } from './TeamUsers';
import { AddUserDialog } from '../Dialogs/AddUserDialog';
import { ConfirmationDialog } from '@/components/shared/ConfirmationDialog';
import {
  GetAllTeamsForUserManagementDocument,
  GetAllTeamsForUserManagementQuery,
  GetUsersByTeamDocument,
  useAddUsersToTeamMutation,
  useDeleteTeamMutation,
} from '@/__generated__/graphql';
import { ActionButton, PanelTitle } from '../styles';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';

type Props = {
  selectedTeam: GetAllTeamsForUserManagementQuery['allTeams'][0] | undefined;
  loading: boolean;
};

export const TeamDetail = ({ selectedTeam, loading }: Props) => {
  const [deleteTeamDialog, setDeleteTeamDialog] = useState(false);
  const [addUserModal, setAddUserModal] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const { teamName, teamId, teamUsersCount, queryVariables } = useMemo(() => {
    return {
      teamName: selectedTeam?.name,
      teamId: selectedTeam?.id,
      teamUsersCount: selectedTeam?.users.length || 0,
      queryVariables: {
        excludeTeamId: selectedTeam?.id || undefined,
      },
    };
  }, [selectedTeam]);

  const [deleteTeam] = useDeleteTeamMutation();
  const [addUsersToTeam] = useAddUsersToTeamMutation();

  const handleToggleAddUserModal = useCallback(() => {
    if (addUserModal) setSelectedUserIds([]);
    setAddUserModal(!addUserModal);
  }, [addUserModal]);

  const handleSelectedUsers = (userId: string) => () => {
    setSelectedUserIds((previousSelected) =>
      previousSelected.includes(userId)
        ? previousSelected.filter((id) => id !== userId)
        : [...previousSelected, userId]
    );
  };

  const handleAddUsers = useCallback(() => {
    if (selectedUserIds.length > 0 && teamId) {
      addUsersToTeam({
        variables: {
          input: {
            teamId,
            userIds: selectedUserIds,
          },
        },
        refetchQueries: [GetAllTeamsForUserManagementDocument, GetUsersByTeamDocument],
        onCompleted: (response) => {
          if (response) {
            handleToggleAddUserModal();
            showSnackBar(`User has been added successfully`, 'success');
          }
        },
      });
    }
  }, [addUsersToTeam, handleToggleAddUserModal, selectedUserIds, showSnackBar, teamId]);

  const handleDeleteTeamDialog = useCallback(() => {
    setDeleteTeamDialog(true);
  }, []);

  const handleDeleteTeam = useCallback(() => {
    setDeleteTeamDialog(false);
    if (teamId) {
      deleteTeam({
        variables: {
          teamId,
        },
        refetchQueries: [GetAllTeamsForUserManagementDocument],
      });
      showSnackBar(`${teamName} has been deleted successfully`, 'success');
    }
  }, [deleteTeam, showSnackBar, teamId, teamName]);

  const handleCloseDialog = useCallback(() => {
    setDeleteTeamDialog(false);
  }, []);

  return (
    <>
      {!loading && !selectedTeam && (
        <Typography variant="body1" fontSize="2.6rem" fontWeight={600} sx={{ textAlign: 'center' }}>
          No teams found
        </Typography>
      )}
      {selectedTeam && (
        <Stack gap={3}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <PanelTitle>{teamName}</PanelTitle>
            <Stack gap={2} direction="row">
              <ActionButton
                variant="text"
                size="medium"
                startIcon={<AddCircleRounded />}
                onClick={handleToggleAddUserModal}
                data-testid="add-user-button"
              >
                Add User To This Team
              </ActionButton>
              <ActionButton
                variant="text"
                size="medium"
                startIcon={<Delete />}
                onClick={handleDeleteTeamDialog}
                disabled={teamUsersCount > 0}
                data-testid="delete-team-button"
              >
                Delete Team
              </ActionButton>
            </Stack>
          </Stack>
          <TeamUsers selectedTeam={selectedTeam} />
        </Stack>
      )}
      {addUserModal && (
        <AddUserDialog
          open={addUserModal}
          handleCheckedUsers={handleSelectedUsers}
          handleAddUsers={handleAddUsers}
          handleToggle={handleToggleAddUserModal}
          checked={selectedUserIds}
          queryVariable={queryVariables}
        />
      )}
      {deleteTeamDialog && (
        <ConfirmationDialog
          isOpen={deleteTeamDialog}
          title="Delete Team"
          description={`Are you sure you want to delete ${teamName}.`}
          confirmButtonText="Delete Team"
          cancelButtonText="Cancel"
          onConfirm={handleDeleteTeam}
          onCancel={handleCloseDialog}
          handleClose={handleCloseDialog}
        />
      )}
    </>
  );
};
